import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { PageHeader } from "@finitless/shared/components";
import { useToast, useBusinesses } from "@finitless/shared/hooks";
import { useApiStore } from "@finitless/shared/stores";
import { getBrandName } from "@finitless/shared/lib";
import { useBusinessStore } from "../../../stores/businessStore";
import {
  useLinkInBioAdmin,
  useLinkInBioSlugAvailability,
} from "../../../hooks/useLinkInBioAdmin";
import { useAiAgents } from "../../../hooks/useAiAgents";
import {
  canSelectWhatsAppAgentForLinkInBio,
  digitsOnly,
  getFinitlessAgentChatUrl,
  getWhatsAppAgentE164,
} from "../../../lib/linkInBio";
import { buildProfileFromRpcData } from "../../../lib/linkInBio/buildProfileFromRpcData";
import { normalizeSecondaryButtonsState } from "../../../lib/linkInBio/defaultSecondaryButtons";
import { getSupabasePublicClient } from "../../../lib/supabasePublicClient";
import { getLinkInBioAdminFunctionUrl } from "../../../lib/linkInBioAdminApi";
import LinkInBioPublicView from "../../linkInBio/LinkInBioPublicView";
import {
  LinkInBioBrowserFrame,
  LinkInBioPhoneFrame,
} from "../../linkInBio/LinkInBioPreviewFrame";
import { Button, Icon, Input, Label, Switch, buttonVariants } from "../../ui";
import Breadcrumb from "../../ui/Navigation/Breadcrumb";
import { cn } from "@/lib/utils";
import { isLinkInBioSlugFormatValid } from "../../../lib/linkInBio/isLinkInBioSlugFormatValid";
import { getRestaurantFieldsFromBusiness } from "../../../lib/linkInBio/getRestaurantFieldsFromBusiness";
import {
  LINK_IN_BIO_DEFAULT_ACCENT_HEX,
  LINK_IN_BIO_TAGLINE_MAX_LENGTH,
  clampLinkInBioTagline,
  normalizeLinkInBioHexColor,
} from "../../../lib/linkInBio/linkInBioTheme";
import { LinkInBioSettingsPageSkeleton } from "./LinkInBioSkeletons";
import {
  buildPublicLinkInBioUrl,
  getLinkInBioPublicBaseUrl,
} from "../../../lib/linkInBio/linkInBioPublicPath";

const PRIMARY_CTA_KEYS = ["chat", "whatsapp", "order"];

/** Wait after typing before calling `check_slug` (public link / slug field). */
const SLUG_AVAILABILITY_DEBOUNCE_MS = 450;

/** Native `<select>` styling aligned with slug `Input` (h-9) + custom chevron like the store picker. */
const linkInBioSelectClassName = cn(
  "border-input bg-background text-foreground focus-visible:ring-ring flex h-9 w-full cursor-pointer appearance-none rounded-md border px-3 py-1 pr-10 text-sm shadow-xs transition-colors",
  "focus-visible:ring-1 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50",
);

const linkInBioSelectChevronIconClassName =
  "text-muted-foreground pointer-events-none absolute top-1/2 right-3 h-4 w-4 -translate-y-1/2";

function normalizePrimaryCtaForForm(raw) {
  const s = String(raw || "");
  if (s === "auto") {
    return "chat";
  }
  if (PRIMARY_CTA_KEYS.includes(s)) {
    return s;
  }
  return "chat";
}

function SortableSecondaryRow({ btn, updateSecondary }) {
  const { t } = useTranslation("linkInBio");
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: btn.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 1 : undefined,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        "border-border flex flex-col gap-3 rounded-lg border p-4 md:flex-row md:items-center",
        isDragging && "bg-muted/30 shadow-md",
      )}
    >
      <div
        className={cn(
          "flex min-w-0 flex-1 gap-3",
          btn.active ? "md:items-start" : "md:items-center",
        )}
      >
        <button
          type="button"
          className={cn(
            "text-muted-foreground hover:text-foreground shrink-0 touch-none rounded-md border border-transparent p-2",
            "cursor-grab active:cursor-grabbing",
            "focus-visible:ring-ring focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none",
          )}
          {...attributes}
          {...listeners}
          aria-label={t("sections.secondaries.dragHandle")}
        >
          <Icon name="GripVertical" className="h-5 w-5" />
        </button>
        <div className="min-w-0 flex-1">
          <p className="font-medium">{btn.label}</p>
          {btn.active ? (
            <Input
              value={btn.url}
              placeholder={t("sections.secondaries.urlPlaceholder")}
              onChange={(e) => updateSecondary(btn.id, { url: e.target.value })}
              className="mt-2"
            />
          ) : null}
        </div>
      </div>
      <div className="flex shrink-0 items-center justify-end gap-2 md:justify-start">
        <Switch
          checked={btn.active}
          onCheckedChange={(v) =>
            updateSecondary(btn.id, { active: Boolean(v) })
          }
        />
      </div>
    </div>
  );
}

function rowToForm(row, business, t) {
  const fromBiz = getRestaurantFieldsFromBusiness(business);
  if (row) {
    return {
      enabled: Boolean(row.enabled),
      slug: String(row.slug || "").toLowerCase(),
      display_name: String(row.display_name ?? ""),
      tagline:
        row.tagline != null ? String(row.tagline) : fromBiz.tagline || "",
      logo_url:
        row.logo_url != null ? String(row.logo_url) : fromBiz.logo_url || "",
      primary_cta: normalizePrimaryCtaForForm(row.primary_cta),
      whatsapp_e164: row.whatsapp_e164 != null ? String(row.whatsapp_e164) : "",
      chat_url: row.chat_url != null ? String(row.chat_url) : "",
      order_url: row.order_url != null ? String(row.order_url) : "",
      secondary_buttons: normalizeSecondaryButtonsState(
        row.secondary_buttons,
        t,
      ),
      theme_logo_bg: normalizeLinkInBioHexColor(
        row.theme_logo_bg != null ? String(row.theme_logo_bg) : null,
        LINK_IN_BIO_DEFAULT_ACCENT_HEX,
      ),
      theme_primary_bg: normalizeLinkInBioHexColor(
        row.theme_primary_bg != null ? String(row.theme_primary_bg) : null,
        LINK_IN_BIO_DEFAULT_ACCENT_HEX,
      ),
    };
  }
  return {
    enabled: true,
    slug: "",
    display_name: "",
    tagline: fromBiz.tagline,
    logo_url: fromBiz.logo_url,
    theme_logo_bg: LINK_IN_BIO_DEFAULT_ACCENT_HEX,
    theme_primary_bg: LINK_IN_BIO_DEFAULT_ACCENT_HEX,
    primary_cta: "chat",
    whatsapp_e164: "",
    chat_url: "",
    order_url: "",
    secondary_buttons: normalizeSecondaryButtonsState([], t),
  };
}

const LinkInBioSettingsPage = () => {
  const { t } = useTranslation("linkInBio");
  const { t: tNav } = useTranslation("navigation");
  const { t: tPublic } = useTranslation("linkInBioPublic");
  const toast = useToast();
  const navigate = useNavigate();
  const { profileId } = useParams();
  const selectedBusiness = useBusinessStore((s) => s.selectedBusiness);
  const businessId = selectedBusiness?.id;
  const apiProject = useApiStore((s) => String(s.project || "").trim());

  const [committedSlug, setCommittedSlug] = useState("");
  const [previewDevice, setPreviewDevice] = useState("phone");
  const [restaurantSourceBusinessId, setRestaurantSourceBusinessId] =
    useState("");
  /** Supabase `business_id` for this profile: store picker on create, or `row.business_id` on edit. */
  const [linkProfileBusinessId, setLinkProfileBusinessId] = useState("");
  const isNewProfile = String(profileId || "") === "new";
  /** Edit: `row.business_id` (via state) after load; fallback to selected store only until then. Create: store picker. */
  const adminBusinessId = useMemo(() => {
    if (isNewProfile) {
      return String(linkProfileBusinessId || "").trim();
    }
    const fromState = String(linkProfileBusinessId || "").trim();
    const fromSelected =
      businessId != null && String(businessId).trim() !== ""
        ? String(businessId).trim()
        : "";
    return fromState || fromSelected;
  }, [isNewProfile, linkProfileBusinessId, businessId]);

  const { data: businessesData, isLoading: businessesLoading } = useBusinesses({
    enabledFilter: "enabled",
    searchTerm: "",
    includePagination: false,
  });
  const businessesList = businessesData?.businesses ?? [];

  const {
    data: row,
    isLoading,
    isError,
    isSuccess,
    error,
    refetch,
    upsertMutation,
    isCreate,
  } = useLinkInBioAdmin(adminBusinessId, profileId);

  const settingsBreadcrumbItems = useMemo(
    () => [
      { label: tNav("sidebar.growth"), disabled: true },
      {
        label: tNav("sidebar.linkInBio"),
        path: "/growth/link-in-bio",
        icon: "Link2",
      },
      {
        label: isCreate ? t("header.createTitle") : t("header.editTitle"),
        active: true,
        disabled: true,
      },
    ],
    [isCreate, t, tNav],
  );

  const [form, setForm] = useState(null);

  useEffect(() => {
    setRestaurantSourceBusinessId("");
    setLinkProfileBusinessId("");
  }, [isCreate, profileId]);

  const { data: agentsData } = useAiAgents({
    page: 1,
    pageSize: 50,
    enabledFilter: "all",
    providerFilter: "all",
  });

  const agents = agentsData?.agents || [];

  const chatAgents = useMemo(
    () =>
      agents.filter(
        (a) =>
          a.provider === "finitless" &&
          a.provider_type === "chat" &&
          a.enabled &&
          getFinitlessAgentChatUrl(a),
      ),
    [agents],
  );

  const whatsAppAgents = useMemo(
    () => agents.filter(canSelectWhatsAppAgentForLinkInBio),
    [agents],
  );

  const matchedChatAgentId = useMemo(() => {
    const url = (form?.chat_url || "").trim();
    if (!url) {
      return "";
    }
    const hit = chatAgents.find(
      (agent) => getFinitlessAgentChatUrl(agent) === url,
    );
    return hit ? String(hit.id) : "";
  }, [form?.chat_url, chatAgents]);

  const matchedWhatsAppAgentId = useMemo(() => {
    const raw = (form?.whatsapp_e164 || "").trim();
    const d = digitsOnly(raw);
    if (d.length < 8) {
      return "";
    }
    const hit = whatsAppAgents.find(
      (agent) => digitsOnly(getWhatsAppAgentE164(agent) || "") === d,
    );
    return hit ? String(hit.id) : "";
  }, [form?.whatsapp_e164, whatsAppAgents]);

  const primaryModes = useMemo(
    () => [
      { key: "chat", label: t("primaryModes.chat") },
      { key: "whatsapp", label: t("primaryModes.whatsapp") },
      { key: "order", label: t("primaryModes.order") },
    ],
    [t],
  );

  useEffect(() => {
    if (!businessId) {
      return;
    }
    if (isCreate) {
      setForm(rowToForm(null, null, t));
      setCommittedSlug("");
      setRestaurantSourceBusinessId("");
      setLinkProfileBusinessId("");
      return;
    }
    if (isLoading) {
      return;
    }
    const next = rowToForm(row, selectedBusiness, t);
    setForm(next);
    setCommittedSlug(row?.slug ? String(row.slug).toLowerCase() : "");
    if (row?.business_id != null && String(row.business_id).trim() !== "") {
      const bid = String(row.business_id);
      setLinkProfileBusinessId(bid);
      setRestaurantSourceBusinessId(bid);
    }
  }, [businessId, row, selectedBusiness, isLoading, t, isCreate]);

  const slugForCheck = form?.slug?.trim().toLowerCase() || "";
  const slugFormatOk = isLinkInBioSlugFormatValid(slugForCheck);
  const slugCheckEnabled = slugFormatOk && slugForCheck !== committedSlug;

  const [debouncedSlugForCheck, setDebouncedSlugForCheck] = useState("");

  useEffect(() => {
    const id = window.setTimeout(() => {
      setDebouncedSlugForCheck(slugForCheck);
    }, SLUG_AVAILABILITY_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [slugForCheck]);

  const slugSyncedWithDebounce = slugForCheck === debouncedSlugForCheck;
  const debouncedSlugFormatOk = isLinkInBioSlugFormatValid(
    debouncedSlugForCheck,
  );
  const slugAvailabilityPending = slugCheckEnabled && !slugSyncedWithDebounce;
  const slugQueryEnabled =
    slugCheckEnabled && debouncedSlugFormatOk && slugSyncedWithDebounce;

  const { data: slugAvailable, isFetching: slugChecking } =
    useLinkInBioSlugAvailability(
      debouncedSlugForCheck,
      slugQueryEnabled,
      row?.id,
    );

  const previewRpcShape = useMemo(() => {
    if (!form) {
      return null;
    }
    return {
      display_name: form.display_name,
      tagline: form.tagline || null,
      logo_url: form.logo_url || null,
      theme_logo_bg: form.theme_logo_bg,
      theme_primary_bg: form.theme_primary_bg,
      primary_cta: form.primary_cta,
      whatsapp_e164: form.whatsapp_e164 || null,
      chat_url: form.chat_url || null,
      order_url: form.order_url || null,
      secondary_buttons: form.secondary_buttons,
    };
  }, [form]);

  const { profile, primaryResolved, primaryLabel } = useMemo(
    () => buildProfileFromRpcData(previewRpcShape, tPublic),
    [previewRpcShape, tPublic],
  );

  const supabaseReady = Boolean(getSupabasePublicClient());
  const functionReady = Boolean(getLinkInBioAdminFunctionUrl());

  const publicBase =
    typeof window !== "undefined" ? getLinkInBioPublicBaseUrl() : "";
  const finitlessProjectForUrl = useMemo(() => {
    const fromRow =
      row?.finitless_project != null
        ? String(row.finitless_project).trim()
        : "";
    if (fromRow) {
      return fromRow;
    }
    return apiProject;
  }, [row?.finitless_project, apiProject]);

  const publicUrl =
    slugForCheck && finitlessProjectForUrl
      ? buildPublicLinkInBioUrl(
          publicBase,
          finitlessProjectForUrl,
          slugForCheck,
        )
      : "";

  const updateSecondary = (id, patch) => {
    setForm((prev) => {
      if (!prev) {
        return prev;
      }
      return {
        ...prev,
        secondary_buttons: prev.secondary_buttons.map((b) =>
          b.id === id ? { ...b, ...patch } : b,
        ),
      };
    });
  };

  const secondaryDragSensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleSecondaryDragEnd = useCallback((event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) {
      return;
    }
    setForm((prev) => {
      if (!prev) {
        return prev;
      }
      const list = [...prev.secondary_buttons].sort(
        (a, b) => a.sort_order - b.sort_order,
      );
      const oldIndex = list.findIndex((b) => b.id === active.id);
      const newIndex = list.findIndex((b) => b.id === over.id);
      if (oldIndex < 0 || newIndex < 0) {
        return prev;
      }
      const reordered = arrayMove(list, oldIndex, newIndex);
      const nextButtons = reordered.map((row, i) => ({
        ...row,
        sort_order: i,
      }));
      return { ...prev, secondary_buttons: nextButtons };
    });
  }, []);

  const handleSave = async () => {
    if (!form) {
      return;
    }
    if (!adminBusinessId) {
      if (isCreate && !String(linkProfileBusinessId || "").trim()) {
        toast.error(t("sections.restaurant.selectStoreToSave"));
      }
      return;
    }
    if (!slugFormatOk) {
      toast.error(t("sections.link.slugInvalid"));
      return;
    }
    if (slugCheckEnabled && slugForCheck !== debouncedSlugForCheck) {
      return;
    }
    if (slugQueryEnabled && slugAvailable === false) {
      toast.error(t("sections.link.slugTaken"));
      return;
    }
    if (form.tagline && form.tagline.length > LINK_IN_BIO_TAGLINE_MAX_LENGTH) {
      toast.error(
        t("sections.restaurant.taglineTooLong", {
          max: LINK_IN_BIO_TAGLINE_MAX_LENGTH,
        }),
      );
      return;
    }
    try {
      const saved = await upsertMutation.mutateAsync({
        business_id: adminBusinessId,
        row: {
          ...(row?.id ? { id: row.id } : {}),
          enabled: form.enabled,
          slug: slugForCheck,
          display_name: form.display_name,
          list_label: null,
          tagline: form.tagline || null,
          logo_url: form.logo_url || null,
          theme_logo_bg: normalizeLinkInBioHexColor(form.theme_logo_bg),
          theme_primary_bg: normalizeLinkInBioHexColor(form.theme_primary_bg),
          primary_cta: normalizePrimaryCtaForForm(form.primary_cta),
          whatsapp_e164: form.whatsapp_e164 || null,
          chat_url: form.chat_url || null,
          order_url: form.order_url || null,
          secondary_buttons: form.secondary_buttons,
        },
      });
      setCommittedSlug(slugForCheck);
      if (isCreate && saved?.id) {
        navigate("/growth/link-in-bio", { replace: true });
      }
      toast.success(t("toasts.saved"));
    } catch {
      toast.error(t("toasts.saveError"));
    }
  };

  if (!businessId) {
    return (
      <div className="text-muted-foreground py-12 text-center text-sm">
        {t("noBusiness")}
      </div>
    );
  }

  if (!supabaseReady || !functionReady) {
    return (
      <div className="space-y-6">
        <Breadcrumb items={settingsBreadcrumbItems} />
        <PageHeader title={t("header.title")} subtitle={t("header.subtitle")} />
        <div className="border-destructive/30 bg-destructive/5 text-destructive rounded-xl border p-4 text-sm">
          <p className="font-semibold">{t("configBanner.title")}</p>
          <p className="text-destructive/90 mt-2">{t("configBanner.body")}</p>
        </div>
      </div>
    );
  }

  const showEditorLoading = (isCreate && !form) || (!isCreate && isLoading);
  const profileNotFound = !isCreate && !isLoading && isSuccess && row == null;

  if (showEditorLoading) {
    return (
      <div className="space-y-6">
        <Breadcrumb items={settingsBreadcrumbItems} />
        <PageHeader title={t("header.title")} subtitle={t("header.subtitle")} />
        <LinkInBioSettingsPageSkeleton />
      </div>
    );
  }

  if (profileNotFound) {
    return (
      <div className="space-y-6">
        <Breadcrumb items={settingsBreadcrumbItems} />
        <PageHeader title={t("header.title")} subtitle={t("header.subtitle")} />
        <div className="border-border rounded-xl border p-6 text-center">
          <p className="text-muted-foreground text-sm">{t("notFound")}</p>
          <Link
            to="/growth/link-in-bio"
            className={cn(
              buttonVariants({ variant: "default" }),
              "mt-4 inline-flex",
            )}
          >
            {t("backToList")}
          </Link>
        </div>
      </div>
    );
  }

  if (!form) {
    return null;
  }

  if (isError) {
    return (
      <div className="space-y-6">
        <Breadcrumb items={settingsBreadcrumbItems} />
        <PageHeader title={t("header.title")} subtitle={t("header.subtitle")} />
        <div className="border-border rounded-xl border p-6 text-center">
          <p className="text-destructive text-sm">{t("loadError")}</p>
          <p className="text-muted-foreground mt-2 text-xs">
            {error?.message || ""}
          </p>
          <Button type="button" className="mt-4" onClick={() => refetch()}>
            {t("actions.retry")}
          </Button>
        </div>
      </div>
    );
  }

  const sortedSecondaries = [...form.secondary_buttons].sort(
    (a, b) => a.sort_order - b.sort_order,
  );

  const previewBlock =
    profile &&
    (previewDevice === "phone" ? (
      <LinkInBioPhoneFrame>
        <LinkInBioPublicView
          profile={profile}
          primaryResolved={primaryResolved}
          primaryLabel={primaryLabel}
          poweredByLabel={tPublic("poweredBy", {
            brand: getBrandName("Finitless"),
          })}
          interactive={false}
          className="w-full"
        />
      </LinkInBioPhoneFrame>
    ) : (
      <LinkInBioBrowserFrame
        url={publicUrl}
        tabLabel={t("sections.preview.browserTab")}
      >
        <LinkInBioPublicView
          profile={profile}
          primaryResolved={primaryResolved}
          primaryLabel={primaryLabel}
          poweredByLabel={tPublic("poweredBy", {
            brand: getBrandName("Finitless"),
          })}
          interactive={false}
          className="w-full"
        />
      </LinkInBioBrowserFrame>
    ));

  return (
    <div className="flex min-h-0 flex-col gap-8 lg:max-h-[calc(100dvh-6.5rem)] lg:overflow-hidden">
      <Breadcrumb items={settingsBreadcrumbItems} />
      <div className="flex shrink-0 flex-col justify-between gap-4 md:flex-row md:items-center">
        <PageHeader
          title={isCreate ? t("header.createTitle") : t("header.editTitle")}
          subtitle={t("header.subtitle")}
        />
        <Button
          type="button"
          onClick={handleSave}
          className="min-w-[8.5rem] px-8"
          disabled={
            upsertMutation.isPending ||
            (isCreate && !String(linkProfileBusinessId || "").trim()) ||
            slugAvailabilityPending ||
            (slugQueryEnabled && slugChecking) ||
            (slugQueryEnabled && slugAvailable === false) ||
            !slugFormatOk
          }
        >
          {upsertMutation.isPending ? t("actions.saving") : t("actions.save")}
        </Button>
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-8 overflow-hidden lg:flex-row lg:items-stretch lg:gap-8">
        <div className="min-h-0 min-w-0 flex-1 space-y-8 overflow-y-auto overscroll-contain pr-1">
          <section className="border-border bg-card text-card-foreground rounded-xl border p-6 shadow-sm">
            <div className="flex items-center justify-between gap-4">
              <div>
                <h2 className="text-lg font-semibold">
                  {t("sections.enable.title")}
                </h2>
                <p className="text-muted-foreground mt-1 text-sm">
                  {t("sections.enable.hint")}
                </p>
              </div>
              <Switch
                checked={form.enabled}
                onCheckedChange={(v) =>
                  setForm((p) => (p ? { ...p, enabled: Boolean(v) } : p))
                }
              />
            </div>
          </section>

          <div className={`space-y-8 ${!form.enabled ? "opacity-60" : ""}`}>
            <section className="border-border bg-card text-card-foreground rounded-xl border p-6 shadow-sm">
              <h2 className="text-lg font-semibold">
                {t("sections.link.title")}
              </h2>
              <p className="text-muted-foreground mt-1 text-sm">
                {t("sections.link.slugHelp")}
              </p>
              <div className="mt-4 flex flex-wrap items-end gap-3">
                <div className="min-w-[200px] flex-1">
                  <Label htmlFor="lib-slug">{t("sections.link.prefix")}</Label>
                  <div className="border-input mt-1 flex rounded-md border shadow-xs">
                    <span className="text-muted-foreground border-input bg-muted flex shrink-0 items-center self-stretch border-r px-3 text-sm">
                      {t("sections.link.urlPrefix", {
                        project: finitlessProjectForUrl || "…",
                      })}
                    </span>
                    <Input
                      id="lib-slug"
                      value={form.slug}
                      onChange={(e) =>
                        setForm((p) =>
                          p
                            ? {
                                ...p,
                                slug: e.target.value
                                  .toLowerCase()
                                  .replace(/\s+/g, "-")
                                  .replace(/[^a-z0-9-]/g, ""),
                              }
                            : p,
                        )
                      }
                      className="border-0 shadow-none focus-visible:ring-0"
                      autoComplete="off"
                    />
                  </div>
                </div>
                {!isCreate && publicUrl ? (
                  <a
                    href={publicUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={cn(
                      buttonVariants({ variant: "outline" }),
                      "h-9 shrink-0 gap-2 px-3 py-0",
                    )}
                  >
                    <Icon name="ExternalLink" className="h-4 w-4" />
                    {t("sections.link.openPublic")}
                  </a>
                ) : null}
              </div>
              {slugForCheck && !slugFormatOk ? (
                <p className="text-destructive mt-2 text-sm font-medium">
                  {t("sections.link.slugInvalid")}
                </p>
              ) : null}
              {slugCheckEnabled &&
              (slugAvailabilityPending ||
                (slugQueryEnabled && slugChecking)) ? (
                <p className="text-muted-foreground mt-2 text-sm">
                  {t("sections.link.checkingSlug")}
                </p>
              ) : null}
              {slugQueryEnabled && !slugChecking && slugAvailable === false ? (
                <p className="text-destructive mt-2 text-sm font-medium">
                  {t("sections.link.slugTaken")}
                </p>
              ) : null}
              {slugFormatOk &&
              (!slugCheckEnabled ||
                (slugQueryEnabled &&
                  !slugChecking &&
                  slugAvailable === true)) ? (
                <p className="text-success mt-2 text-sm font-medium">
                  {t("sections.link.slugAvailable")}
                </p>
              ) : null}
            </section>

            <section className="border-border bg-card text-card-foreground rounded-xl border p-6 shadow-sm">
              <h2 className="text-lg font-semibold">
                {t("sections.restaurant.title")}
              </h2>
              <div className="mt-4 grid gap-4 md:grid-cols-2">
                <div className="md:col-span-2">
                  <Label htmlFor="lib-source-biz">
                    {t("sections.restaurant.businessSelect")}
                  </Label>
                  <div className="relative mt-1">
                    <select
                      id="lib-source-biz"
                      className={linkInBioSelectClassName}
                      value={restaurantSourceBusinessId}
                      onChange={(e) => {
                        const id = e.target.value;
                        setRestaurantSourceBusinessId(id);
                        if (isCreate) {
                          setLinkProfileBusinessId(id ? id : "");
                        }
                        const b = businessesList.find(
                          (x) => String(x.id) === id,
                        );
                        if (!b) {
                          return;
                        }
                        setForm((p) =>
                          p
                            ? {
                                ...p,
                                ...getRestaurantFieldsFromBusiness(b),
                              }
                            : p,
                        );
                      }}
                      disabled={
                        businessesLoading || businessesList.length === 0
                      }
                      aria-label={t("sections.restaurant.businessSelect")}
                    >
                      <option value="">
                        {t("sections.restaurant.businessSelectPlaceholder")}
                      </option>
                      {businessesList.map((b) => (
                        <option key={b.id} value={String(b.id)}>
                          {b.name != null && String(b.name).trim() !== ""
                            ? String(b.name)
                            : String(b.id)}
                        </option>
                      ))}
                    </select>
                    <Icon
                      name="ChevronDown"
                      className={linkInBioSelectChevronIconClassName}
                      aria-hidden
                    />
                  </div>
                  <p className="text-muted-foreground mt-1 text-xs">
                    {t("sections.restaurant.businessSelectHint")}
                  </p>
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="lib-name">
                    {t("sections.restaurant.displayName")}
                  </Label>
                  <Input
                    id="lib-name"
                    value={form.display_name}
                    onChange={(e) =>
                      setForm((p) =>
                        p ? { ...p, display_name: e.target.value } : p,
                      )
                    }
                    className="mt-1 w-full"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="lib-tagline">
                    {t("sections.restaurant.tagline")}
                  </Label>
                  <textarea
                    id="lib-tagline"
                    value={form.tagline}
                    maxLength={LINK_IN_BIO_TAGLINE_MAX_LENGTH}
                    rows={4}
                    onChange={(e) => {
                      const next = clampLinkInBioTagline(
                        e.target.value,
                        LINK_IN_BIO_TAGLINE_MAX_LENGTH,
                      );
                      setForm((p) => (p ? { ...p, tagline: next } : p));
                    }}
                    className={cn(
                      "border-input text-foreground placeholder:text-muted-foreground focus-visible:ring-ring mt-1 flex min-h-[100px] w-full resize-y rounded-md border bg-transparent px-3 py-2 text-sm shadow-sm transition-colors",
                      "focus-visible:ring-1 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50",
                    )}
                  />
                  <p className="text-muted-foreground mt-1 text-xs">
                    {t("sections.restaurant.taglineCounter", {
                      current: form.tagline.length,
                      max: LINK_IN_BIO_TAGLINE_MAX_LENGTH,
                    })}
                  </p>
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="lib-logo">
                    {t("sections.restaurant.logoUrl")}
                  </Label>
                  <Input
                    id="lib-logo"
                    value={form.logo_url}
                    onChange={(e) =>
                      setForm((p) =>
                        p ? { ...p, logo_url: e.target.value } : p,
                      )
                    }
                    className="mt-1"
                  />
                </div>
              </div>
            </section>

            <section className="border-border bg-card text-card-foreground rounded-xl border p-6 shadow-sm">
              <h2 className="text-lg font-semibold">
                {t("sections.appearance.title")}
              </h2>
              <p className="text-muted-foreground mt-1 text-sm">
                {t("sections.appearance.hint")}
              </p>
              <div className="mt-4 flex flex-col gap-6 sm:flex-row sm:flex-wrap sm:items-end">
                <div>
                  <Label htmlFor="lib-theme-logo">
                    {t("sections.appearance.logoBackground")}
                  </Label>
                  <div className="mt-2 flex flex-wrap items-center gap-3">
                    <input
                      id="lib-theme-logo"
                      type="color"
                      value={normalizeLinkInBioHexColor(form.theme_logo_bg)}
                      onChange={(e) =>
                        setForm((p) =>
                          p ? { ...p, theme_logo_bg: e.target.value } : p,
                        )
                      }
                      className="border-input h-10 w-14 cursor-pointer rounded-md border bg-transparent p-1 shadow-xs"
                      aria-label={t("sections.appearance.logoBackground")}
                    />
                    <span className="text-muted-foreground font-mono text-xs">
                      {normalizeLinkInBioHexColor(form.theme_logo_bg)}
                    </span>
                  </div>
                </div>
                <div>
                  <Label htmlFor="lib-theme-primary">
                    {t("sections.appearance.primaryButtonBackground")}
                  </Label>
                  <div className="mt-2 flex flex-wrap items-center gap-3">
                    <input
                      id="lib-theme-primary"
                      type="color"
                      value={normalizeLinkInBioHexColor(form.theme_primary_bg)}
                      onChange={(e) =>
                        setForm((p) =>
                          p ? { ...p, theme_primary_bg: e.target.value } : p,
                        )
                      }
                      className="border-input h-10 w-14 cursor-pointer rounded-md border bg-transparent p-1 shadow-xs"
                      aria-label={t(
                        "sections.appearance.primaryButtonBackground",
                      )}
                    />
                    <span className="text-muted-foreground font-mono text-xs">
                      {normalizeLinkInBioHexColor(form.theme_primary_bg)}
                    </span>
                  </div>
                </div>
              </div>
            </section>

            <section className="border-border bg-card text-card-foreground rounded-xl border p-6 shadow-sm">
              <h2 className="text-lg font-semibold">
                {t("sections.primary.title")}
              </h2>
              <p className="text-muted-foreground mt-1 text-sm">
                {t("sections.primary.hint")}
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                {primaryModes.map((m) => (
                  <button
                    key={m.key}
                    type="button"
                    onClick={() =>
                      setForm((p) => (p ? { ...p, primary_cta: m.key } : p))
                    }
                    className={cn(
                      "rounded-lg border px-3 py-2 text-sm font-medium transition",
                      form.primary_cta === m.key
                        ? "border-primary bg-primary text-primary-foreground"
                        : "border-border bg-background hover:bg-muted",
                    )}
                  >
                    {m.label}
                  </button>
                ))}
              </div>

              {form.primary_cta === "chat" ? (
                <div className="mt-4 space-y-3">
                  <div>
                    <Label htmlFor="lib-chat-agent">
                      {t("sections.primary.chatAgentSelect")}
                    </Label>
                    <div className="relative mt-1">
                      <select
                        id="lib-chat-agent"
                        className={linkInBioSelectClassName}
                        value={matchedChatAgentId}
                        onChange={(e) => {
                          const v = e.target.value;
                          setForm((prev) => {
                            if (!prev) {
                              return prev;
                            }
                            if (!v) {
                              return { ...prev, chat_url: "" };
                            }
                            const agent = chatAgents.find(
                              (a) => String(a.id) === v,
                            );
                            const url = agent
                              ? getFinitlessAgentChatUrl(agent) || ""
                              : "";
                            return { ...prev, chat_url: url };
                          });
                        }}
                      >
                        <option value="">
                          {t("sections.primary.chatAgentPlaceholder")}
                        </option>
                        {chatAgents.map((a) => (
                          <option key={a.id} value={String(a.id)}>
                            {t("sections.primary.chatAgentOption", {
                              id: String(a.id),
                            })}
                          </option>
                        ))}
                      </select>
                      <Icon
                        name="ChevronDown"
                        className={linkInBioSelectChevronIconClassName}
                        aria-hidden
                      />
                    </div>
                  </div>
                  {chatAgents.length === 0 ? (
                    <p className="text-muted-foreground text-sm">
                      {t("sections.primary.noChatAgents")}{" "}
                      <Link
                        to="/ai-agents"
                        className="text-primary font-medium underline-offset-4 hover:underline"
                      >
                        {t("sections.primary.manageAgents")}
                      </Link>
                    </p>
                  ) : null}
                  {chatAgents.length === 0 || !matchedChatAgentId ? (
                    <div>
                      <Label htmlFor="lib-chat-manual">
                        {t("sections.primary.chatUrlManual")}
                      </Label>
                      <Input
                        id="lib-chat-manual"
                        value={form.chat_url}
                        onChange={(e) =>
                          setForm((p) =>
                            p ? { ...p, chat_url: e.target.value } : p,
                          )
                        }
                        className="mt-1"
                        placeholder={t("sections.primary.urlPlaceholderHttps")}
                      />
                    </div>
                  ) : null}
                </div>
              ) : null}

              {form.primary_cta === "whatsapp" ? (
                <div className="mt-4 space-y-3">
                  <div>
                    <Label htmlFor="lib-wa-agent">
                      {t("sections.primary.whatsappAgentSelect")}
                    </Label>
                    <div className="relative mt-1">
                      <select
                        id="lib-wa-agent"
                        className={linkInBioSelectClassName}
                        value={matchedWhatsAppAgentId}
                        onChange={(e) => {
                          const v = e.target.value;
                          setForm((prev) => {
                            if (!prev) {
                              return prev;
                            }
                            if (!v) {
                              return { ...prev, whatsapp_e164: "" };
                            }
                            const agent = whatsAppAgents.find(
                              (a) => String(a.id) === v,
                            );
                            const e164 = agent
                              ? getWhatsAppAgentE164(agent) || ""
                              : "";
                            return { ...prev, whatsapp_e164: e164 };
                          });
                        }}
                      >
                        <option value="">
                          {t("sections.primary.whatsappAgentPlaceholder")}
                        </option>
                        {whatsAppAgents.map((a) => (
                          <option key={a.id} value={String(a.id)}>
                            {t("sections.primary.whatsappAgentOption", {
                              phone: getWhatsAppAgentE164(a) || "",
                              id: String(a.id),
                            })}
                          </option>
                        ))}
                      </select>
                      <Icon
                        name="ChevronDown"
                        className={linkInBioSelectChevronIconClassName}
                        aria-hidden
                      />
                    </div>
                  </div>
                  {whatsAppAgents.length === 0 ? (
                    <p className="text-muted-foreground text-sm">
                      {t("sections.primary.noWhatsappAgents")}{" "}
                      <Link
                        to="/ai-agents"
                        className="text-primary font-medium underline-offset-4 hover:underline"
                      >
                        {t("sections.primary.manageAgents")}
                      </Link>
                    </p>
                  ) : null}
                  {whatsAppAgents.length === 0 || !matchedWhatsAppAgentId ? (
                    <div>
                      <Label htmlFor="lib-wa-manual">
                        {t("sections.primary.whatsappManual")}
                      </Label>
                      <Input
                        id="lib-wa-manual"
                        value={form.whatsapp_e164}
                        onChange={(e) =>
                          setForm((p) =>
                            p ? { ...p, whatsapp_e164: e.target.value } : p,
                          )
                        }
                        className="mt-1"
                        placeholder={t("sections.primary.whatsappPlaceholder")}
                      />
                    </div>
                  ) : null}
                </div>
              ) : null}

              {form.primary_cta === "order" ? (
                <div className="mt-4">
                  <Label htmlFor="lib-order">
                    {t("sections.primary.orderUrl")}
                  </Label>
                  <Input
                    id="lib-order"
                    value={form.order_url}
                    onChange={(e) =>
                      setForm((p) =>
                        p ? { ...p, order_url: e.target.value } : p,
                      )
                    }
                    className="mt-1"
                    placeholder={t("sections.primary.urlPlaceholderHttps")}
                  />
                </div>
              ) : null}
            </section>

            <section className="border-border bg-card text-card-foreground rounded-xl border p-6 shadow-sm">
              <h2 className="text-lg font-semibold">
                {t("sections.secondaries.title")}
              </h2>
              <p className="text-muted-foreground mt-1 text-sm">
                {t("sections.secondaries.hint")}
              </p>
              <DndContext
                sensors={secondaryDragSensors}
                collisionDetection={closestCenter}
                onDragEnd={handleSecondaryDragEnd}
              >
                <SortableContext
                  items={sortedSecondaries.map((b) => b.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="mt-4 space-y-3">
                    {sortedSecondaries.map((btn) => (
                      <SortableSecondaryRow
                        key={btn.id}
                        btn={btn}
                        updateSecondary={updateSecondary}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            </section>
          </div>
        </div>

        <aside className="w-full shrink-0 lg:min-h-0 lg:w-[min(100%,380px)] lg:max-w-[min(100%,380px)] lg:overflow-y-auto lg:overscroll-contain xl:w-[min(100%,420px)] xl:max-w-[min(100%,420px)]">
          <section className="border-border bg-card text-card-foreground rounded-xl border p-6 shadow-sm">
            <h2 className="text-lg font-semibold">
              {t("sections.preview.title")}
            </h2>
            <p className="text-muted-foreground mt-1 text-sm">
              {t("sections.preview.deviceToggle")}
            </p>
            <div className="mt-6 flex flex-col items-center gap-5">
              <div
                className="flex flex-wrap items-center justify-center gap-2"
                role="group"
                aria-label={t("sections.preview.deviceToggle")}
              >
                <Button
                  type="button"
                  variant={previewDevice === "phone" ? "default" : "outline"}
                  size="sm"
                  className="gap-2"
                  aria-pressed={previewDevice === "phone"}
                  onClick={() => setPreviewDevice("phone")}
                >
                  <Icon name="Smartphone" className="h-4 w-4 shrink-0" />
                  {t("sections.preview.phone")}
                </Button>
                <Button
                  type="button"
                  variant={previewDevice === "web" ? "default" : "outline"}
                  size="sm"
                  className="gap-2"
                  aria-pressed={previewDevice === "web"}
                  onClick={() => setPreviewDevice("web")}
                >
                  <Icon name="Monitor" className="h-4 w-4 shrink-0" />
                  {t("sections.preview.web")}
                </Button>
              </div>
              <div className="flex w-full justify-center">{previewBlock}</div>
            </div>
          </section>
        </aside>
      </div>
    </div>
  );
};

export default LinkInBioSettingsPage;
